import { createSelector } from 'reselect';

import { selectSubCompany } from './subCompany.selectors';

const selectTpoCertificationState = createSelector(
  selectSubCompany,
  subCompany => subCompany?.tpoCertification || {},
);

export const selectTpoCertificationOwnerSubCompanyId = createSelector(
  selectTpoCertificationState,
  state => state.ownerSubCompanyId ?? null,
);

// Flat TpoCertificationDto (status / submittedAt / approvedAt / expiresAt / ids).
export const selectTpoCertificationActive = createSelector(
  selectTpoCertificationState,
  state => state.cert || null,
);

// Flat TpoCertificationPageDto (pageStatus / certifiedUntil / cycleType) — the action unwraps
// getActivePage's `{ activeCertification }` envelope, so this is the canonical page-info shape.
export const selectTpoCertificationPageInfo = createSelector(
  selectTpoCertificationState,
  state => state.pageInfo || null,
);

// The path-form active read returns 204 once a cert is finalized/promoted, while the
// query-form page read still surfaces it. Fall back to a cert-shaped view of pageInfo
// so finalized certs keep their status, id, and expiry for every consumer.
export const selectTpoCertificationActiveResolved = createSelector(
  selectTpoCertificationActive,
  selectTpoCertificationPageInfo,
  (cert, pageInfo) => {
    if (cert) {
      return cert;
    }

    if (!pageInfo?.pageStatus) {
      return null;
    }

    return {
      tpoCertificationId: pageInfo.tpoCertificationId ?? null,
      status: pageInfo.pageStatus,
      expiresAt: pageInfo.certifiedUntil ?? null,
    };
  },
);

export const selectTpoCertificationProfile = createSelector(
  selectTpoCertificationState,
  state => state.profile || null,
);

export const selectTpoCertificationDenials = createSelector(
  selectTpoCertificationState,
  state => state.denials ?? null,
);

export const selectTpoCertificationDenialsCertificationId = createSelector(
  selectTpoCertificationState,
  state => state.denialsCertificationId ?? null,
);

// Provenance ({ tpoSignupConfigurationId, name, isDefault }) for the config the cycle ran under.
export const selectTpoCertificationSignupConfig = createSelector(
  selectTpoCertificationState,
  state => state.signupConfig || null,
);

// TpoStepEvaluationResponseDto — the server-evaluated steps this cert cycle actually ran.
export const selectTpoCertificationEvaluatedSteps = createSelector(
  selectTpoCertificationState,
  state => state.evaluatedSteps || null,
);

export const selectTpoCertificationEvaluatedStepsCertificationId = createSelector(
  selectTpoCertificationState,
  state => state.evaluatedStepsCertificationId ?? null,
);

// TpoDocumentDto[] for the cycle; null when the read has not settled or was refused.
export const selectTpoCertificationDocuments = createSelector(
  selectTpoCertificationState,
  state => state.documents ?? null,
);

export const selectTpoCertificationHistory = createSelector(
  selectTpoCertificationState,
  state => state.history || [],
);

export const selectTpoCertificationCanStartNewCycle = createSelector(
  selectTpoCertificationState,
  state => state.canStartNewCycle ?? null,
);
